import { useCallback, useMemo } from 'react'

import { Event } from 'types/event'
import { Notification, NotificationType } from 'types/notification'

import { useClientSubscriptions } from './useClientSubscriptions'
import { useCurrentUser } from './useCurrentUser'
import { useGroup } from './useGroup'
import { useGroupEvents } from './useGroupEvents'
import { useQuery } from './useQuery'
import { useUserEvents } from './useUserEvents'

export const useNotifications = () => {
  const { data: currentUser } = useCurrentUser()
  const { data: subscriptions } = useClientSubscriptions({ userId: currentUser?.id })
  const subscribedGroupIds = useMemo(
    () => (subscriptions || []).map((sub) => sub.program.id || ''),
    [subscriptions],
  )

  const { data: userGroup } = useGroup({ userId: currentUser?.id })
  const eventGroupIds = useMemo(
    () => (userGroup ? [userGroup.id, ...subscribedGroupIds] : subscribedGroupIds),
    [subscribedGroupIds, userGroup],
  )

  const {
    data: groupEventsData,
    isLoading: isGroupEventsLoading,
    fetchNextPage: fetchNextGroupEvents,
    isFetching: isGroupEventsFetching,
    isFetchingNextPage: isGroupEventsFetchingNextPage,
  } = useGroupEvents({
    groupIds: eventGroupIds,
    eventObject: 'all',
    limit: 30,
  })

  const groupEvents = useMemo(
    () => groupEventsData?.pages.reduce((acc, page) => acc.concat(page), []) ?? [],
    [groupEventsData?.pages],
  )

  const {
    data: userEventsData,
    isLoading: isUserEventsLoading,
    fetchNextPage: fetchNextUserEvents,
    isFetching: isUserEventsFetching,
    isFetchingNextPage: isUserEventsFetchingNextPage,
  } = useUserEvents({
    userId: currentUser?.id,
    limit: 30,
  })
  const userEvents = useMemo(
    () => userEventsData?.pages.reduce((acc, page) => acc.concat(page), []) ?? [],
    [userEventsData?.pages],
  )

  const events = useMemo(() => {
    let finalEvents: Array<Event> = [...groupEvents, ...userEvents]
    finalEvents = finalEvents.filter((event) => {
      if (
        ['highFive', 'like', 'follow', 'accepted', 'comment'].includes(event.eventType) &&
        event.creator?.id !== currentUser?.id
      ) {
        return true
      }

      if (event.eventType === 'completion') {
        if (event?.post?.creator?.id !== currentUser?.id) {
          return false
        }
        if (!event.post?.isArchived) {
          return true
        }
      } else if (event.eventObject === 'post' && event.eventType === 'create') {
        return false
      }

      return false
    })

    const uniqueIds: string[] = []
    finalEvents = finalEvents.filter((event) => {
      const isDuplicate = uniqueIds.includes(event.id)

      if (!isDuplicate) {
        uniqueIds.push(event.id)

        return true
      }

      return false
    })

    finalEvents.sort((a, b) => {
      return b.created - a.created
    })

    return finalEvents
  }, [currentUser?.id, groupEvents, userEvents])

  const notifications = useMemo(() => {
    const returnNotifications: Array<Notification> = events.map((event) => {
      const secondPersonId =
        event.eventType === 'accepted' && event.creator?.id === currentUser?.id
          ? event.viewers.users.filter((userId) => userId !== currentUser?.id)[0]
          : undefined

      return {
        eventId: event.id,
        user: event.creator,
        secondUserId: secondPersonId,
        post: event.post,
        completion: event.completion && { id: event.objectId, ...event.completion },
        type: event.eventType as NotificationType,
        timestamp: event.created,
      }
    })

    return returnNotifications
  }, [currentUser?.id, events])

  const isNotificationsLoading = useMemo(
    () => isGroupEventsLoading || isUserEventsLoading,
    [isGroupEventsLoading, isUserEventsLoading],
  )

  const fetchNextNotifications = useCallback(() => {
    fetchNextGroupEvents()
    fetchNextUserEvents()
  }, [fetchNextGroupEvents, fetchNextUserEvents])

  const notificationsQuery = useQuery<Array<Notification>>(['notifications'], () => notifications, {
    enabled: !!currentUser?.id,
  })

  return {
    notificationsQuery,
    data: notifications,
    isLoading: isNotificationsLoading,
    isFetching: isGroupEventsFetching || isUserEventsFetching,
    isFetchingNextPage: isGroupEventsFetchingNextPage || isUserEventsFetchingNextPage,
    fetchNextPage: fetchNextNotifications,
  }
}
